// Shared chrome for every authenticated route: top bar + left nav sidebar.
// Visual language tracks ServiceAI's NavMenu (ServiceAI.Client/Layout/NavMenu.razor):
// the hamburger toggle lives INSIDE the sidebar with the tenant name beside it, a
// primary "New Workflow" action sits at the top, nav items are grouped under
// uppercase section labels, and Settings is pinned to the bottom. Tokens come
// from @shared/theme; dark-mode nav text uses the dimmer --fg-muted, brightening
// to --fg on hover/active (ServiceAI's --sai-fg-muted → --sai-fg treatment).

import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate, Outlet } from 'react-router-dom';
import { AppBar, AppBarSection, AppBarSpacer, Avatar } from '@progress/kendo-react-layout';
import { Loader } from '@progress/kendo-react-indicators';
import { SvgIcon } from '@progress/kendo-react-common';
import {
  menuIcon,
  plusIcon,
  homeIcon,
  shapesIcon,
  rocketIcon,
  cpuIcon,
  puzzlePieceIcon,
  robotIcon,
  clockIcon,
  clockArrowRotateIcon,
  commentIcon,
  chevronLeftIcon,
  infoCircleIcon,
  chartColumnClusteredIcon,
  usersIcon,
  lockIcon,
  slidersIcon,
  qrCodeIcon,
  connectorIcon,
  bookIcon,
  targetIcon,
  buildingsIcon,
  dataIcon,
  bellIcon,
  type SVGIcon,
} from '@progress/kendo-svg-icons';
import { gearFilledIcon, bellFilledIcon } from './headerIcons';
import { useMediaQuery, WIDE_QUERY } from '@shared/theme';
import { useAuth } from '../lib/auth';
import { apiFetch } from '../lib/apiClient';
import { ImpersonationBanner } from './ImpersonationBanner';
import { OrionSupport } from './OrionSupport';
import { About } from './About';
import { ActionRequiredBubble } from './ActionRequiredBubble';
import { AskAiLauncher } from './AskAiLauncher';
import { GettingStarted } from './GettingStarted';
import { loadBeamer } from '../lib/beamer';
import { useWaitingConversations } from '../lib/useWaitingConversations';
import { useStandingAttention } from '../lib/useStandingAttention';
import './AppShell.css';

// Two initials from the display name, else the email's first letter — mirrors
// ServiceAI's header avatar (initials when there's no profile photo).
function initialsFor(displayName: string, email: string): string {
  const name = displayName.trim();
  if (name) {
    const parts = name.split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return (email.trim()[0] ?? '?').toUpperCase();
}

interface NavEntry {
  text: string;
  route: string;
  // SVG icons — the legacy `k-i-*` font-icon classes don't render under the
  // Fluent theme (KendoReact ships SVG icons; the font-icon CSS isn't loaded).
  svgIcon: SVGIcon;
  /**
   * Optional count badge (generic; the Pulsars entry is the first user). Shown
   * as a number in the trigger token, hidden at zero, and folded into the
   * button's accessible name ("Pulsars, 3 need attention") so it never relies on
   * colour or on the visual number alone.
   */
  badge?: { count: number; label: (count: number) => string };
}

interface NavSection {
  // Uppercase label above the group; null for the ungrouped top block.
  label: string | null;
  items: NavEntry[];
}

// Header shown above a contextual sidebar's sections (ServiceAI's eyebrow/title).
interface ContextualHeader {
  eyebrow: string;
  title: string;
}

// Ungrouped top block, then named sections; Settings is pinned to the bottom
// separately (see render). History links to the tenant-wide run history — every
// run across all workflows (/runs), drilling into the per-run output blade.
const SECTIONS: NavSection[] = [
  {
    label: null,
    items: [
      { text: 'Home', route: '/', svgIcon: homeIcon },
      { text: 'AI Playground', route: '/ai-playground', svgIcon: commentIcon },
    ],
  },
  {
    label: 'Actions',
    items: [
      { text: 'Playbooks', route: '/playbooks', svgIcon: targetIcon },
      { text: 'Workflows', route: '/workflows', svgIcon: shapesIcon },
    ],
  },
  {
    label: 'Foundations',
    items: [
      { text: 'Knowledge', route: '/knowledge', svgIcon: bookIcon },
      { text: 'Agents', route: '/agents', svgIcon: robotIcon },
      { text: 'Extensions', route: '/extensions', svgIcon: puzzlePieceIcon },
      { text: 'Companies', route: '/companies', svgIcon: buildingsIcon },
    ],
  },
  {
    label: 'Schedule',
    items: [
      { text: 'Routines', route: '/routines', svgIcon: clockIcon },
      { text: 'History', route: '/runs', svgIcon: clockArrowRotateIcon },
    ],
  },
  {
    label: 'Your Infrastructure',
    items: [
      { text: 'Deployments', route: '/deployments', svgIcon: rocketIcon },
      { text: 'Runners', route: '/runners', svgIcon: cpuIcon },
    ],
  },
];

// Contextual sidebar shown under /settings — mirrors ServiceAI's SettingsNavMenu.
const SETTINGS_SECTIONS: NavSection[] = [
  {
    label: 'General',
    items: [
      { text: 'Overview', route: '/settings', svgIcon: infoCircleIcon },
      { text: 'Usage', route: '/settings/usage', svgIcon: chartColumnClusteredIcon },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { text: 'Team', route: '/settings/team', svgIcon: usersIcon },
      { text: 'Integrations', route: '/settings/integrations', svgIcon: connectorIcon },
    ],
  },
];

// Contextual sidebar shown under /personal-settings — mirrors ServiceAI's
// PersonalSettingsNavMenu. Email + display name are NOT here (edited on Team).
const PERSONAL_SECTIONS: NavSection[] = [
  {
    label: 'General',
    items: [
      { text: 'Preferences', route: '/personal-settings', svgIcon: slidersIcon },
      { text: 'Security', route: '/personal-settings/security', svgIcon: qrCodeIcon },
      { text: 'Notifications', route: '/personal-settings/notifications', svgIcon: bellIcon },
      { text: 'Password', route: '/personal-settings/password', svgIcon: lockIcon },
    ],
  },
];

const SETTINGS_HEADER: ContextualHeader = { eyebrow: 'Settings', title: 'Account' };
const PERSONAL_HEADER: ContextualHeader = { eyebrow: 'Personal', title: 'Your account' };

type SidebarMode = 'main' | 'settings' | 'personal';

function sidebarModeFor(path: string): SidebarMode {
  if (path === '/settings' || path.startsWith('/settings/')) return 'settings';
  if (path === '/personal-settings' || path.startsWith('/personal-settings/')) return 'personal';
  return 'main';
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useAuth();
  // Sidebar width. Two inputs, deliberately kept apart:
  //
  //   isWide          — the viewport tier. At `medium` (< 1024px) the nav
  //                     auto-rails to icons, buying back ~200px of content
  //                     width, which is the single biggest lever at 768px.
  //   userExpanded    — an explicit hamburger press. null means "never touched",
  //                     so the width follows the viewport.
  //
  // The explicit choice is NOT persisted, so a tablet session can never write
  // back over a desktop preference (and vice versa).
  //
  // The override records WHICH ROUTE it was made on, which is what makes the
  // tablet behaviour fall out by derivation instead of needing an effect to
  // clear it: at `medium` the override applies only while you are still on that
  // route, so navigating away re-rails the nav the way a drawer closes behind
  // you. At `wide` the sidebar is a permanent column, so the override applies
  // everywhere and a desktop collapse sticks for the session.
  const isWide = useMediaQuery(WIDE_QUERY);
  const [navOverride, setNavOverride] = useState<{ expanded: boolean; path: string } | null>(null);
  const overrideApplies =
    navOverride !== null && (isWide || navOverride.path === location.pathname);
  const expanded = overrideApplies ? navOverride.expanded : isWide;
  // Which sidebar to show: the primary nav, or a contextual Settings /
  // Personal Settings nav (ServiceAI parity — MainLayout swaps the sidebar
  // when you enter those areas).
  const mode = sidebarModeFor(location.pathname);
  // Remember the last primary-nav location so the contextual sidebars' "Back"
  // button returns there (defaults to Home).
  const preContextUrlRef = useRef('/');
  useEffect(() => {
    if (mode === 'main') preContextUrlRef.current = location.pathname + location.search;
  }, [mode, location.pathname, location.search]);

  const [tenantName, setTenantName] = useState('');

  // Beamer "What's New" widget — binds to the #beamer-button trigger in the
  // header (ServiceAI parity). Loaded here so it's scoped to the authenticated
  // shell, never the login page.
  useEffect(() => loadBeamer(), []);

  // Tenant name for the sidebar header — /api/me only carries tenantId, so pull
  // the display name from /api/me/tenant once on mount.
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ name: string }>('/api/me/tenant')
      .then((t) => {
        if (!cancelled) setTenantName(t.name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Orion Pulsars (standing agents): the nav entry exists only while the region
  // has the feature on, sits above Playbooks in Actions, and carries the
  // attention count (pending batches + questions + halted pulsars).
  const attention = useStandingAttention(
    state.status === 'authenticated' && state.user.features?.pulsars === true,
    state.status === 'authenticated' ? `${state.user.userId}:${state.user.tenantId}` : '',
  );
  // AI Playground conversations waiting on the user (an open question, input or approval).
  const waiting = useWaitingConversations();
  const mainSections = useMemo<NavSection[]>(() => {
    const base = !waiting.enabled
      ? SECTIONS
      : SECTIONS.map((section) => ({
          ...section,
          items: section.items.map((item) =>
            item.route === '/ai-playground'
              ? { ...item, badge: { count: waiting.count, label: (n: number) => `${n} waiting for you` } }
              : item,
          ),
        }));
    if (!attention.enabled) return base;
    return base.map((section) =>
      section.label === 'Actions'
        ? {
            ...section,
            items: [
              {
                text: 'Pulsars',
                route: '/pulsars',
                svgIcon: dataIcon,
                badge: {
                  count: attention.count,
                  label: (n: number) => `${n} need${n === 1 ? 's' : ''} attention`,
                },
              },
              ...section.items,
            ],
          }
        : section,
    );
  }, [attention.enabled, attention.count, waiting.enabled, waiting.count]);

  // The nav set the current mode renders.
  const activeSections =
    mode === 'settings' ? SETTINGS_SECTIONS : mode === 'personal' ? PERSONAL_SECTIONS : mainSections;

  // Highlight only the MOST specific matching nav entry. A plain startsWith made
  // /ai-playground/history light up both AI Playground and History — so pick the longest
  // route that matches on a path boundary (=== route or starts with route + '/').
  const selectedRoute = useMemo(() => {
    const path = location.pathname;
    let best = '';
    for (const section of activeSections) {
      for (const { route } of section.items) {
        const match = route === '/' ? path === '/' : path === route || path.startsWith(`${route}/`);
        if (match && route.length > best.length) best = route;
      }
    }
    return best;
  }, [location.pathname, activeSections]);

  const userEmail = state.status === 'authenticated' ? state.user.email : '';
  const displayName = state.status === 'authenticated' ? state.user.displayName : '';
  const initials = initialsFor(displayName, userEmail);

  // Force-change-on-first-login: an invited teammate created with a temporary
  // password is pinned to the Password page until they change it. All app
  // hooks above have run, so this early return is hook-safe. Logout still
  // works — it lives in the Personal Settings header rendered on that page.
  const mustChangePassword = state.status === 'authenticated' && state.user.mustChangePassword;
  if (mustChangePassword && location.pathname !== '/personal-settings/password') {
    return <Navigate to="/personal-settings/password" replace />;
  }

  const renderItem = (item: NavEntry) => {
    const selected = item.route === selectedRoute;
    const badgeCount = item.badge?.count ?? 0;
    const accessibleName = item.badge && badgeCount > 0 ? `${item.text}, ${item.badge.label(badgeCount)}` : undefined;
    return (
      <button
        key={item.route}
        type="button"
        className={`nav-item${selected ? ' selected' : ''}`}
        aria-current={selected ? 'page' : undefined}
        aria-label={accessibleName}
        title={accessibleName ?? item.text}
        onClick={() => navigate(item.route)}
      >
        <span className="nav-icon" aria-hidden="true">
          <SvgIcon icon={item.svgIcon} />
        </span>
        <span className="nav-label">{item.text}</span>
        {badgeCount > 0 && (
          <span className="nav-badge" aria-hidden="true">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="app-shell">
      <ImpersonationBanner />
      <AppBar className="app-shell-bar">
        <AppBarSection>
          <button
            type="button"
            className="brand"
            aria-label="CloudRadial AutomationAI — home"
            onClick={() => navigate('/')}
          >
            <img
              className="brand-mark"
              src="/icons/light/automationai-96.png"
              alt=""
              aria-hidden="true"
              width={30}
              height={30}
            />
            <span className="brand-name">
              <strong>CloudRadial</strong>
              <span className="brand-name-thin">AutomationAI</span>
            </span>
          </button>
          <span className="brand-alpha-badge">Beta</span>
        </AppBarSection>
        <AppBarSpacer />
        <AppBarSection className="header-right">
          {/* Header action icons — all share one plain-button shape so they size
              and colour identically (ServiceAI parity: ExtraLarge, full-brightness
              --fg). Rendering SvgIcon directly avoids KendoReact Button's own icon
              sizing. */}
          {/* Settings — moved into the header (ServiceAI parity); no longer a
              sidebar item. */}
          <button
            type="button"
            className="header-icon-btn"
            aria-label="Settings"
            title="Settings"
            onClick={() => navigate('/settings')}
          >
            <SvgIcon icon={gearFilledIcon} />
          </button>
          {/* Getting Started — onboarding checklist (ServiceAI parity). Its
              red badge counts the setup steps still to do. */}
          <GettingStarted />
          {/* Action Required no longer lives here — it's a floating bubble mounted
              at the app-shell level (see <ActionRequiredBubble /> below) so a
              full-screen test overlay can't bury it. */}
          {/* Beamer "What's New" trigger — left of the user info, ServiceAI parity.
              Beamer binds the click + renders its unread badge on this element. */}
          <button
            type="button"
            id="beamer-button"
            className="header-icon-btn"
            aria-label="What's New"
            title="What's New"
          >
            <SvgIcon icon={bellFilledIcon} />
          </button>
          {/* About — version + "Check for updates" force-refresh + product links
              (ServiceAI parity: the question/info icon in the app bar). */}
          <About />
          {/* Email + avatar both open Personal Settings (ServiceAI parity);
              Logout now lives on that page's header, not in the app bar. */}
          <button
            type="button"
            className="header-user-email-link"
            onClick={() => navigate('/personal-settings')}
          >
            {userEmail}
          </button>
          <button
            type="button"
            className="avatar-link"
            aria-label="Personal settings"
            title={userEmail}
            onClick={() => navigate('/personal-settings')}
          >
            <Avatar type="text" themeColor="primary" size="medium" rounded="full" border>
              {initials}
            </Avatar>
          </button>
          {/* Orion product-support — constellation icon (rightmost, ServiceAI
              parity: it sits to the right of the avatar). Opens a blade
              embedding the configured Orion portal. */}
          <OrionSupport />
        </AppBarSection>
      </AppBar>

      <div className="app-shell-body">
        <nav
          className={`app-sidebar${mode === 'main' && !expanded ? ' collapsed' : ''}`}
          aria-label={mode === 'settings' ? 'Settings' : mode === 'personal' ? 'Personal settings' : 'Primary'}
        >
          {mode === 'main' ? (
            <>
              <button
                type="button"
                className="menu-toggle-btn"
                title={tenantName || 'Toggle navigation'}
                aria-label="Toggle navigation"
                aria-expanded={expanded}
                onClick={() => setNavOverride({ expanded: !expanded, path: location.pathname })}
              >
                <span className="nav-icon" aria-hidden="true">
                  <SvgIcon icon={menuIcon} />
                </span>
                <span className="nav-label tenant-name">{tenantName}</span>
              </button>

              <button
                type="button"
                className="new-workflow-btn"
                title="AI Generate Workflow"
                onClick={() =>
                  // Send the author to the Workflows page and auto-open the
                  // "AI Generate" blade there (consumed via nav state). The list
                  // page owns the AI-Generate flow, so this stays one code path.
                  navigate('/workflows', { state: { openAiGenerate: true } })
                }
              >
                <span className="nav-icon" aria-hidden="true">
                  <SvgIcon icon={plusIcon} />
                </span>
                <span className="nav-label">New Workflow</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="back-btn"
                onClick={() => navigate(preContextUrlRef.current)}
              >
                <span className="nav-icon" aria-hidden="true">
                  <SvgIcon icon={chevronLeftIcon} />
                </span>
                <span className="nav-label">Back</span>
              </button>
              <div className="settings-header">
                <div className="eyebrow">{(mode === 'settings' ? SETTINGS_HEADER : PERSONAL_HEADER).eyebrow}</div>
                <div className="title">{(mode === 'settings' ? SETTINGS_HEADER : PERSONAL_HEADER).title}</div>
              </div>
            </>
          )}

          <div className="nav-scroll">
            {activeSections.map((section, i) => (
              <div className="nav-section" key={section.label ?? `top-${i}`}>
                {section.label && <div className="section-label">{section.label}</div>}
                {section.items.map(renderItem)}
              </div>
            ))}
          </div>

        </nav>

        <main className="app-shell-main">
          {/* One Suspense boundary for every lazy route (routes.tsx). Nested
              lazy children (e.g. personal-settings sub-pages) bubble up to here,
              so a single fallback covers the whole authed page area. */}
          <Suspense
            fallback={
              <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <Loader size="large" type="converging-spinner" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Action Required — a floating help bubble pinned above ALL overlays (the
          Designer test panel, blades). Appears only when runs await a human and
          vanishes when the last item is resolved; mounted here so it's global to
          every authenticated route. */}
      <ActionRequiredBubble />

      {/* AI Playground command palette — ⌘K / Ctrl+K from any authenticated page
          opens a goal prompt that hands off to the AI Playground page. Mounted here so the
          shortcut is global to every authenticated route. */}
      <AskAiLauncher />

    </div>
  );
}
